// @ts-strict-ignore

import {ToastType} from 'common/components/toast/types';
import {Store} from 'common/report/stores/store';
import {SectionItem} from 'common/components/items-menu/types';
import {assert, baseComparator} from 'common/util';
import {ComponentLifetimeStartEventName} from 'common/util/component-helpers';
import {LoadingType} from 'common/util/workflow-timing';
import {OrderBy} from 'common/util/array';
import {propertyDefinitionsClient} from 'common/report/queries/app-api/property-definitions-client';
import {eventDefinitionsClient} from 'common/report/queries/app-api/event-definitions-client';
import {ApiResponseError} from 'common/util/shared-client';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {Toast} from 'common/components/toast-area';
import {MENU_HEIGHT_REDUCTION, SHORT_WINDOW_HEIGHT} from 'common/constants';

import * as PropertiesMenu from '../properties-menu/types';
import {CohortSegment} from '../segment-menu/types';
import {CHILD_MENU_HEIGHT, TRACKING_EVENTS} from '../constants';

/**
 * Builds cohort definitions from a list of cohorts
 */
export function getCohortFromPropertyItem(item: PropertiesMenu.Item): CohortSegment {
  assert(PropertiesMenu.isCohort(item));

  let cohort;
  if (PropertiesMenu.isInlineCohort(item)) {
    cohort = item.value.cohort;
    cohort = {raw_cohort: {name: item.value.cohort.name || item.label, ...cohort.raw_cohort}, negated: false};
  } else {
    cohort = {id: item.value};
    cohort = {...(item.dataGroupId ? {data_group_id: item.dataGroupId} : {}), ...cohort};
  }

  return {cohort: {...cohort, name: item.label, negated: false}};
}

/**
 * Updates a given Entity type description
 * @param entityStore
 * @param newDescription
 * @param storeKey
 * @param updateRequestObj
 * @param undoRequestObj
 * @param entityType
 */
export async function updateEntityDescription({
  entityStore,
  newDescription,
  storeKey,
  updateRequestObj,
  undoRequestObj,
  entityType,
  addToast,
  track,
}: {
  entityStore: Store<any>;
  newDescription: string;
  storeKey: string;
  updateRequestObj: Record<string, any>;
  undoRequestObj: Record<string, any>;
  entityType: string;
  addToast: (toast: Toast) => void;
  track: (...args) => void;
}) {
  const descriptionBeforeUpdate = entityStore.value[storeKey]?.description ?? null;

  const updateStoreWithDescription = (description: string) =>
    entityStore.update({
      [storeKey]: {
        ...entityStore.value[storeKey],
        description,
      },
    });

  const undoCallback = async () => {
    try {
      updateStoreWithDescription(descriptionBeforeUpdate);
      await entityStore.patch({
        ...undoRequestObj,
      });
    } catch {
      addToast({
        message: (i18n) => i18n.t(`query-builder.undo-description-failed`),
        isError: true,
        type: ToastType.ERROR,
      });
      updateStoreWithDescription(newDescription);
    }
  };

  track(
    TRACKING_EVENTS.EDIT_DESCRIPTION,
    {
      'New description': newDescription,
      'Old description': descriptionBeforeUpdate,
      'Access point': `context panel`,
      'Entity type': entityType,
    },
    false,
  );

  updateStoreWithDescription(newDescription);
  const storeValue = entityStore.value[storeKey];
  try {
    const response = await patchEntityDescription(updateRequestObj);
    // There is an issue with the some stores not updating values on patch. This means name is null when creating a new item.
    // fallback to the response value.
    addToast({
      message: (i18n) => i18n.t(`query-builder.row-updated`, {name: storeValue.name || response.name}),
      type: ToastType.SUCCESS,
      undoable: true,
      undoCallback,
    });
  } catch {
    addToast({
      message: (i18n) => i18n.t(`query-builder.update-description-failed`),
      isError: true,
      type: ToastType.ERROR,
    });
    updateStoreWithDescription(descriptionBeforeUpdate);
  }
}

async function patchEntityDescription(params) {
  // Entities are either events, properties or cohorts
  let apiClient;
  if (params.propertyDefinition || params.eventDefinition) {
    apiClient = params.propertyDefinition ? propertyDefinitionsClient : eventDefinitionsClient;
  } else {
    apiClient = cohortsClient;
  }

  const result = await apiClient.query(params, {method: `PATCH`});
  if (result.status === `error`) {
    throw new ApiResponseError(result, result.error);
  }

  return result;
}

export function sortItemsByLabel<T extends SectionItem>(
  items: T[],
  {order}: {order: OrderBy} = {order: OrderBy.Ascending},
) {
  // todo: i18n
  return items.sort(baseComparator({order, transform: (item: T) => item.label.toString().toLowerCase()}));
}

export enum OpenedMenuComponent {
  ItemsMenu = `items_menu`,
  LoadingSkeleton = `loading_skeleton`,
}
export function getOpenedMenuComponent(ev: CustomEvent, component: HTMLElement): OpenedMenuComponent {
  function isSkeletonRenderedEvent(event: CustomEvent, cmp: HTMLElement): boolean {
    return (
      event.detail.eventName.includes(ComponentLifetimeStartEventName.LoadingAdded) &&
      event.detail.props.type === LoadingType.Skeleton &&
      event.detail.props.parentPath.includes(cmp.tagName.toLowerCase())
    );
  }

  function isItemsMenuRenderedEvent(event: CustomEvent, cmp: HTMLElement): boolean {
    return (
      event.detail.eventName.includes(ComponentLifetimeStartEventName.ComponentAdded) &&
      event.detail.props.sourceComponent === `mp-items-menu` &&
      event.detail.props.parentPath.includes(cmp.tagName.toLowerCase())
    );
  }

  if (isItemsMenuRenderedEvent(ev, component)) {
    return OpenedMenuComponent.ItemsMenu;
  } else if (isSkeletonRenderedEvent(ev, component)) {
    return OpenedMenuComponent.LoadingSkeleton;
  }
  return null;
}

export function isShortWindow(): boolean {
  return window.innerHeight <= SHORT_WINDOW_HEIGHT;
}

export function getChildMenuHeight(): number {
  return isShortWindow() ? CHILD_MENU_HEIGHT - MENU_HEIGHT_REDUCTION : CHILD_MENU_HEIGHT;
}
